import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { openCashierSession, getCashierBoard, payOrder } from "./cashierApi";
import AddPointsPanel from "./AddPointsPanel";
import CelebrationBanner from "./CelebrationBanner";
import GuestStrip from "./GuestStrip";
import { parseApiDate } from "../../utils/datetime";
import "./cashier-display.css";

/** Minutes after which an UNPAID ticket is flagged as ageing on the board. */
const STALE_MIN = 15;

/**
 * QR Loyalty & Ordering — standalone Cashier page (/cashier/:venueCode on THIS
 * SPA's host, e.g. app.popuppush.com — the popuppush.com apex is the marketing
 * site and serves its homepage at every path).
 *
 * PIN-gated like the kitchen display (same venue PIN + scoped token). Shows the
 * day's orders with price + total, a one-tap "Mark Paid" (which awards the guest's
 * loyalty points), and today's running revenue. REST-polled so revenue + paid
 * state stay fresh without a socket. Honey & Ink, bilingual EN/AR.
 *
 * P5 filled in the rest of the till. Everything below the revenue line comes out
 * of the SAME polled snapshot as the tickets, deliberately and with no RTDB:
 *
 *  • a milestone cheer raised at Mark-paid, for the cashier to fire or wave off
 *    while the guest is still standing there (the F-14 fix);
 *  • who is in the room, with till-side reward redemption — the customer card
 *    stays view-only, which is the entire reason a signed token in a URL is an
 *    acceptable identity;
 *  • Add Points on the whole bill, for the counter and waiter sales that never
 *    touch the QR menu.
 *
 * One request means the board can never contradict itself: a guest chip and the
 * ticket beside it were computed from the same read.
 */

const sessKey = (code) => `cds_sess::${String(code).toLowerCase()}`;

export default function CashierDisplay() {
  const { venueCode } = useParams();
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage || i18n.language || "en").startsWith("ar");

  const [session, setSession] = useState(null);
  const [pin, setPin] = useState("");
  const [gateErr, setGateErr] = useState("");
  const [gateBusy, setGateBusy] = useState(false);

  const [orders, setOrders] = useState([]);
  const [revenue, setRevenue] = useState(0);
  const [rewards, setRewards] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [celebrations, setCelebrations] = useState([]);
  const [pickedGuest, setPickedGuest] = useState(null);
  const [busyIds, setBusyIds] = useState({});
  const [tick, setTick] = useState(0);
  const paidLocal = useRef(new Set()); // ids paid optimistically (don't let a stale poll un-pay)
  // Cheers already acted on locally. The server resolves them, but the next
  // poll can be up to five seconds away and a banner that lingers after the tap
  // invites a second tap.
  const resolvedCheers = useRef(new Set());

  /* ── restore a still-valid session ─────────────────────────────────────── */
  useEffect(() => {
    try {
      const s = JSON.parse(sessionStorage.getItem(sessKey(venueCode)) || "null");
      if (s?.token && s?.exp > Date.now()) setSession(s);
    } catch (_) {}
  }, [venueCode]);

  const applyBoard = useCallback((data) => {
    if (!data) return;
    let arr = (data.orders || []).slice();
    // Keep an optimistically-paid card paid until the server agrees.
    if (paidLocal.current.size) {
      arr = arr.map((o) => (paidLocal.current.has(o.id) ? { ...o, paid: true } : o));
      arr.forEach((o) => { if (o.paid) paidLocal.current.delete(o.id); });
    }
    setOrders(arr);
    if (typeof data.revenue === "number") setRevenue(data.revenue);
    if (Array.isArray(data.rewards)) setRewards(data.rewards);
    if (Array.isArray(data.checkins)) setCheckins(data.checkins);
    if (Array.isArray(data.celebrations)) {
      setCelebrations(data.celebrations.filter((c) => !resolvedCheers.current.has(c.id)));
    }
  }, []);

  /* ── poll the board ────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!session?.token) return;
    let alive = true;
    const load = () => getCashierBoard(session.token)
      .then((d) => { if (alive) applyBoard(d); })
      .catch((err) => { if (err?.response?.status === 401) exit(); });
    load();
    const poll = setInterval(load, 5000);
    return () => { alive = false; clearInterval(poll); };
  }, [session, applyBoard]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── clock heartbeat ───────────────────────────────────────────────────── */
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const submitPin = async (e) => {
    e.preventDefault();
    if (pin.length < 4) return setGateErr(t("cashier.err_pin_short"));
    setGateBusy(true); setGateErr("");
    try {
      const res = await openCashierSession(venueCode, pin);
      const s = {
        token: res.token,
        companyId: res.company_id,
        venueName: res.venue_name,
        currencySymbol: res.currency_symbol || "",
        // Spend rate drives the Add Points preview; 0 = venue hasn't opted in.
        rate: Number(res.points_per_currency_unit) || 0,
        exp: Date.now() + (res.expires_in || 3600) * 1000,
      };
      try { sessionStorage.setItem(sessKey(venueCode), JSON.stringify(s)); } catch (_) {}
      // The session response IS a board snapshot — same shape, same keys — so
      // the first paint is complete rather than an empty board that fills in on
      // the first poll five seconds later.
      applyBoard(res);
      setSession(s);
    } catch (err) {
      const code = err?.response?.data?.error;
      setGateErr(code === "invalid_pin" ? t("cashier.err_pin") : t("cashier.err_venue"));
    } finally {
      setGateBusy(false);
    }
  };

  const exit = () => {
    try { sessionStorage.removeItem(sessKey(venueCode)); } catch (_) {}
    setSession(null); setOrders([]); setRevenue(0); setPin("");
    setRewards([]); setCheckins([]); setCelebrations([]); setPickedGuest(null);
  };

  const pay = async (order) => {
    if (busyIds[order.id] || order.paid) return;
    setBusyIds((b) => ({ ...b, [order.id]: true }));
    paidLocal.current.add(order.id);
    setOrders((os) => os.map((o) => (o.id === order.id ? { ...o, paid: true } : o)));
    try {
      // Paying returns the whole refreshed board — the credit, the now-open
      // redemption gate and any milestone cheer all land together, in the same
      // response as the paid flag.
      applyBoard(await payOrder(session.token, order.id));
    } catch (err) {
      paidLocal.current.delete(order.id);
      setOrders((os) => os.map((o) => (o.id === order.id ? { ...o, paid: false } : o)));
      if (err?.response?.status === 401) exit();
    } finally {
      setBusyIds((b) => { const n = { ...b }; delete n[order.id]; return n; });
    }
  };

  // Record only — the banner keeps rendering its own "✓ On the screens" until
  // the next poll drops it (the server has already resolved the suggestion, so
  // it stops being offered). Filtering it out here instead would make the card
  // vanish on the tap, which reads as the tap having gone nowhere.
  const resolveCheer = useCallback((id) => {
    resolvedCheers.current.add(id);
  }, []);

  // A redemption changes the guest's balance, which changes which rewards they
  // can still afford. Patch the chip immediately rather than leave a Redeem
  // button they can no longer pay for on screen until the next poll.
  const applyRedemption = useCallback((res) => {
    const c = res?.customer;
    if (!c?.id) return;
    setCheckins((gs) => gs.map((g) => (g.id === c.id ? { ...g, balance: c.balance, can_redeem: c.can_redeem } : g)));
  }, []);

  const toggleLang = () => i18n.changeLanguage(isAr ? "en" : "ar");
  const localItem = (o) => (isAr && o.item_name_ar ? o.item_name_ar : o.item_name);
  const whereChip = (o) => (o.table_number ? `🍽️ ${t("cashier.table")} ${o.table_number}` : (o.name ? `👤 ${o.name}` : null));

  /**
   * How old this ticket is. The kitchen has always shown it; the cashier never
   * did, and the cashier is the one who needs it most — an UNPAID ticket ageing
   * on the board is money walking out of the door, and there was no way to tell
   * a sale placed thirty seconds ago from one placed forty minutes ago.
   *
   * `created_at` is a millisecond epoch NUMBER (Carbon::valueOf()), so it goes
   * through parseApiDate rather than arithmetic on the raw value — subtracting a
   * number from a string is NaN, which is exactly how the kitchen's timer once
   * rendered "NaN min" for every ticket.
   */
  const elapsed = (o) => {
    const placed = parseApiDate(o.created_at);
    if (!placed) return null;
    const mins = Math.floor((Date.now() - placed.getTime()) / 60000);
    return {
      text: mins < 1 ? t("cashier.just_now") : `${mins} ${t("cashier.min")}`,
      // Only an UNPAID ticket can be "late" — a paid one is closed business and
      // its age is history, not a call to action.
      warn: mins >= STALE_MIN && !o.paid,
    };
  };

  const clock = useMemo(() => {
    try { return new Date().toLocaleTimeString(isAr ? "ar" : "en", { hour: "numeric", minute: "2-digit" }); }
    catch (_) { return ""; }
  }, [tick, isAr]); // eslint-disable-line react-hooks/exhaustive-deps

  const paidCount = orders.filter((o) => o.paid).length;

  /* ── PIN gate ──────────────────────────────────────────────────────────── */
  if (!session) {
    return (
      <div className="cds-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="cds-gate">
          <form className="cds-gate-card" onSubmit={submitPin}>
            <div className="glyph">💳</div>
            <h1>{t("cashier.gate_title")}</h1>
            <div className="sub">{t("cashier.gate_sub")}</div>
            <input className="cds-pin" type="password" inputMode="numeric" autoComplete="off" maxLength={8}
              value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="••••"
              aria-label={t("cashier.pin_label")} autoFocus />
            {gateErr ? <div className="cds-gate-err">{gateErr}</div> : null}
            <button className="cds-gate-btn" type="submit" disabled={gateBusy}>
              {gateBusy ? t("cashier.opening") : t("cashier.open")}
            </button>
            <button type="button" className="cds-lang" onClick={toggleLang}>{isAr ? "English" : "العربية"}</button>
          </form>
        </div>
      </div>
    );
  }

  /* ── board ─────────────────────────────────────────────────────────────── */
  return (
    <div className="cds-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="cds">
        <div className="cds-top">
          <div className="title">{session.venueName} · {t("cashier.title")}</div>
          <div className="meta">
            <span className="mono">{clock}</span>
            <button type="button" onClick={toggleLang}>{isAr ? "EN" : "ع"}</button>
            <button type="button" onClick={exit}>{t("cashier.exit")}</button>
          </div>
        </div>

        <div className="cds-rev">
          <span className="lbl">{t("cashier.today_revenue")}</span>
          <span className="val">{session.currencySymbol} {Number(revenue).toFixed(2)}</span>
          <span className="paidcount">{t("cashier.paid_count", { paid: paidCount, total: orders.length })}</span>
        </div>

        {/* A milestone reached on the payment that just went through — first,
            because it is the only thing here that goes stale. */}
        <CelebrationBanner
          token={session.token}
          celebrations={celebrations}
          onResolved={resolveCheer}
        />

        {/* Who is in the room: redeem a reward, or send them to Add Points. */}
        <GuestStrip
          token={session.token}
          checkins={checkins}
          rewards={rewards}
          currencySymbol={session.currencySymbol}
          onPickForPoints={setPickedGuest}
          onRedeemed={applyRedemption}
        />

        {/* Search anyone — the FALLBACK route to a redemption for a guest who
            neither ordered today nor tapped their card, plus whole-bill points
            for counter/waiter sales that never hit the QR flow. */}
        <AddPointsPanel
          token={session.token}
          currencySymbol={session.currencySymbol}
          rate={session.rate}
          rewards={rewards}
          presetCustomer={pickedGuest}
          onRedeemed={applyRedemption}
        />

        <div className="cds-grid">
          {orders.length === 0 ? (
            <div className="cds-empty"><div className="big">🧾</div><div>{t("cashier.empty")}</div></div>
          ) : orders.map((o) => {
            const el = elapsed(o);
            return (
            <div key={o.id} className={`cds-card${o.paid ? " paid" : ""}${el?.warn ? " stale" : ""}`}>
              <div className="cds-top-row">
                <span className="cds-no">#{o.no}</span>
                {el && <span className={`cds-age mono${el.warn ? " warn" : ""}`}>{el.text}</span>}
              </div>
              {whereChip(o) ? <span className="cds-where">{whereChip(o)}</span> : null}
              <div className="cds-item"><b>{o.qty}×</b> {localItem(o)}</div>
              <div className="cds-total">{session.currencySymbol} {Number(o.total).toFixed(2)}</div>
              {o.paid ? (
                <span className="cds-paid-badge">✓ {t("cashier.paid")}</span>
              ) : (
                <button className="cds-pay" disabled={!!busyIds[o.id]} onClick={() => pay(o)}>
                  {busyIds[o.id] ? <><span className="cds-spin" /> {t("cashier.processing")}</> : t("cashier.mark_paid")}
                </button>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
